/* tslint:disable:max-line-length */
import {UntilDestroy} from '@ngneat/until-destroy';
import {Injectable} from '@angular/core';
import {FaultsImportService} from '../../faults-import.service';
import {FaultsImportStepperService} from './fault-import-stepper.service';
import {getFileHeader, isZipHeader} from '@docma-mm-core/utils/utils';
import JSZip from 'jszip';
import * as ExcelJS from 'exceljs';
import {MmTranslateService} from '../../../../mm-translate.service';
import {ConfigProvider, MydocmaNumberPipe} from '@mydocma/common';
import {IMmConfig} from '@docma-mm-core/project/project.interface';
import {getLocaleNumberSymbol, NumberSymbol} from '@angular/common';


@UntilDestroy()
@Injectable()
export class SelectFileService {

  private MAX_IMPORT_FILE_SIZE_IN_GB = 2;
  maxCountFaultsImport = 50000;

  constructor(private readonly faultsImportService: FaultsImportService,
              private readonly importStepperService: FaultsImportStepperService,
              private translateService: MmTranslateService,
              private readonly numberpipe: MydocmaNumberPipe,
              private configService: ConfigProvider<IMmConfig>) {

    if (this.configService.getConfigData().mmConfig.maxCountFaultsImport) {
      this.maxCountFaultsImport = this.configService.getConfigData().mmConfig.maxCountFaultsImport;
    }
  }

  public async preValidate(file: File) {
    if (file == null) {
      this.importStepperService.selectedFile = null;
      return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.NotSupportedFileType}`) };
    }

    const fileType = await this.detectFileType(file);
    let fileSize: number;

    if (fileType === 'zip') {
      this.importStepperService.selectedFile = await this.getFileListAndExtractImportFileFromZip(file);
      if (this.importStepperService.selectedFile.ImportFile == null) {
        this.importStepperService.selectedFile = null;
        return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.NoImportFileFoundInZip}`) };
      }
      fileSize = this.importStepperService.selectedFile.ZipFile?.size;
      this.importStepperService.selectedFile.IsPreValidationValid = true;
    }
    else if (fileType === 'xlsx') {
      this.importStepperService.selectedFile = { ImportFile: file, Files: [], IsPreValidationValid: true, ZipFile: null };
      fileSize = this.importStepperService.selectedFile.ImportFile?.size;
    }
    else {
      this.importStepperService.selectedFile = null;
      return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.NotSupportedFileType}`) };
    }

    if (fileSize > (this.MAX_IMPORT_FILE_SIZE_IN_GB * 1024 * 1024 * 1024)) {
      return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.MaxFileSizeExceeded}`, {maxFileSize: this.MAX_IMPORT_FILE_SIZE_IN_GB}) };
    }

    const workbook = new ExcelJS.Workbook();
    const fileBuffer = await this.importStepperService.selectedFile.ImportFile.arrayBuffer();

    try {
      await workbook.xlsx.load(fileBuffer);
    }
    catch (ex) {
      return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.InvalidXlsxFileFormat}`) };
    }

    const worksheet = workbook.worksheets[0];

    const nonEmptyRows: ExcelJS.Row[] = [];

    worksheet.eachRow(row => {
      if (this.isNonEmptyRow(row)) {
        nonEmptyRows.push(row);
      }
    });

    if (nonEmptyRows.length === 0) {
      return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.WorkbookEmpty}`) };
    }

    if ((nonEmptyRows.length - 1) > this.maxCountFaultsImport) {
      const maxCountFaultsImportInternal = this.numberpipe.transform(this.maxCountFaultsImport);
      return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.MaxCountDataRows}`, {maxRows: maxCountFaultsImportInternal}) };
    }

    const headerRow = nonEmptyRows[0];
    if (headerRow.cellCount === 0) {
      return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.NoFields}`) };
    }

    const headerCells = Object.values(headerRow.values).map(x => x?.toString()?.toLowerCase());

    const mandatoryFields = Object.values(this.faultsImportService.mandatoryFieldDict).filter(x => x.Id !== 'Structure').map(x => (x.Project ?? x.Global).toString().toLowerCase());

    const missingMandatoryFields = mandatoryFields.filter(field => !headerCells.includes(field));
    const hasAllMandatoryFields = missingMandatoryFields.length === 0;

    if (!hasAllMandatoryFields) {
      return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.MissingMandatoryFields}`, {columns: missingMandatoryFields.join(', ')}) };
    }

    const permittedFields = Object.values(this.faultsImportService.permittedFieldDict).map(x => (x.Project ?? x.Global).toString().toLowerCase());
    const hasPermittedFields = permittedFields.some((cell: any) => !!headerCells.includes(cell));
    if (!hasPermittedFields) {
      return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.HasAnyPermittedField}`) };
    }

    if (nonEmptyRows.length < 2) {
      return { isValid: false, error: this.translateService.instant(`MM.fault.import.steps.selectFileStep.error${PreValidationErrorEnum.NoDataRow}`) };
    }

    return { isValid: true, error: null };
  }

  private isNonEmptyRow(row: ExcelJS.Row): boolean {
    let hasValue = false;

    // By default, eachCell visits only cells with values.
    row.eachCell(cell => {
      if (hasValue) {
        return;
      }

      const value = cell.value;

      if (value === null || value === undefined) {
        return;
      }

      if (typeof value === 'string') {
        hasValue = value.trim().length > 0;
        return;
      }

      if (typeof value === 'object' && 'richText' in value) {
        hasValue = value.richText.some(
          part => part.text.trim().length > 0
        );
        return;
      }

      // Numbers, dates, booleans, formulas, errors, hyperlinks, etc.
      hasValue = true;
    });

    return hasValue;
  }

  private async getFileListAndExtractImportFileFromZip(file: File): Promise<{ ImportFile: File | null, Files: string[], ZipFile: File | null }> {
    const zip = new JSZip();
    const content = await zip.loadAsync(file);

    const files: string[] = [];

    // Base name of ZIP without .zip
    const zipBaseName = file.name.replace(/\.zip$/i, '');

    let importFile: File | null = null;

    for (const relativePath in content.files) {

      const zipEntry = content.files[relativePath];

      // Skip directories for extraction logic
      if (zipEntry.dir) { continue; }

      // ✅ Only files directly under root
      const isRootFile = !relativePath.includes('/') && !relativePath.includes('\\');

      const entryName = relativePath;
      const entryBaseName = entryName.replace(/\.xlsx$/i, '');

      const isMatchingName =
        entryBaseName === zipBaseName &&
        entryName.toLowerCase().endsWith('.xlsx');

      if (isMatchingName && isRootFile) {
        const blob = await zipEntry.async('blob');

        importFile = new File([blob], entryName, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      }
      else {
        // ✅ Collect ALL files (ignore directories)
        if (!zipEntry.dir) {
          files.push(relativePath);
        }
      }
    }

    return { ImportFile: importFile, Files: files, ZipFile: file };
  }

  async detectFileType(file: File): Promise<'xlsx' | 'zip' | 'unknown'> {
    const header = await getFileHeader(file);

    if (!isZipHeader(header)) { return 'unknown'; }

    const zip = await new JSZip().loadAsync(file);
    const isXlsx = Object.keys(zip.files).some(path =>
      path.toLowerCase() === 'xl/workbook.xml'
    );
    return isXlsx ? 'xlsx' : 'zip';
  }

}

export enum PreValidationErrorEnum {
  NotSupportedFileType = 'NotSupportedFileType',
  NoImportFileFoundInZip = 'NoImportFileFoundInZip',
  MaxFileSizeExceeded = 'MaxFileSizeExceeded',
  InvalidXlsxFileFormat = 'InvalidXlsxFileFormat',
  WorkbookEmpty = 'WorkbookEmpty',
  NoFields = 'NoFields',
  HasAnyPermittedField = 'HasAnyPermittedField',
  MissingMandatoryFields = 'MissingMandatoryFields',
  NoDataRow = 'NoDataRow',
  MaxCountDataRows = 'MaxCountDataRows'

}
